import React, { useState, useMemo } from "react";
import {
  MessageCircle,
  Clock,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useODO from "../../hooks/cs/useODO";
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAuthStore } from "../../store/authStore";

const ODOSection = () => {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { odos, isLoading, isLoadingMore, error, hasMore, loadMore, refresh } =
    useODO();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  console.log("odos", odos);

  useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
    threshold: 200,
  });

  const filteredODOs = useMemo(() => {
    if (statusFilter === "all") return odos;
    return odos.filter((odo) => odo.process_status === statusFilter);
  }, [odos, statusFilter]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "답변대기":
        return {
          icon: <Clock className="w-4 h-4" />,
          color: "text-orange-600 bg-orange-100",
          text: "답변대기",
        };
      case "답변완료":
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          color: "text-green-600 bg-green-100",
          text: "답변완료",
        };
      default:
        return {
          icon: <MessageCircle className="w-4 h-4" />,
          color: "text-gray-600 bg-gray-100",
          text: status,
        };
    }
  };

  const getPreviewText = (description: string, maxLength: number = 80) => {
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + "...";
  };

  const handleODOClick = (odoId: string) => {
    navigate(`/cs/odo/${odoId}`);
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          로그인이 필요합니다
        </h3>
        <p className="text-gray-600 mb-6">
          1:1 문의를 이용하려면 먼저 로그인해주세요.
        </p>
        <button
          onClick={() => navigate("/auth/signin")}
          className="px-6 py-2 bg-emerald-500 text-white rounded-lg"
        >
          로그인하기
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="문의 내역을 불러오는 중..." size="lg" />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 text-lg">{error}</p>
        <button
          onClick={refresh}
          className="mt-4 px-6 py-2 bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 text-white rounded-lg hover:from-teal-600 hover:via-green-600 hover:to-emerald-600 transition-all duration-200"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">내 문의 내역</h2>
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { value: "all", label: "전체", icon: null },
                {
                  value: "답변대기",
                  label: "답변대기",
                  icon: <Clock className="w-3 h-3" />,
                },
                {
                  value: "답변완료",
                  label: "답변완료",
                  icon: <CheckCircle className="w-3 h-3" />,
                },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    statusFilter === option.value
                      ? "bg-white text-teal-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filteredODOs.map((odo) => {
          const statusInfo = getStatusInfo(odo.process_status);

          return (
            <div
              key={odo.id}
              onClick={() => handleODOClick(odo.id)}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900 flex-1 pr-4">
                  {odo.title}
                </h3>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-2 leading-relaxed">
                {getPreviewText(odo.description)}
              </p>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(odo.created_at)}
                  </div>
                </div>

                {odo.reference_image && (
                  <div className="text-xs text-teal-600">첨부파일</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isLoadingMore && (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent"></div>
            <span className="text-gray-600">더 많은 문의를 불러오는 중...</span>
          </div>
        </div>
      )}

      {filteredODOs.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {statusFilter === "all"
              ? "문의 내역이 없습니다"
              : `${statusFilter} 문의가 없습니다`}
          </h3>
          <p className="text-gray-600 mb-6">
            궁금한 점이 있으시면 언제든 문의해주세요.
          </p>
          <button
            onClick={() => navigate("/cs/odo/create")}
            className="px-8 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-200"
          >
            1:1 문의하기
          </button>
        </div>
      )}
    </div>
  );
};

export default ODOSection;
